import { injectable } from '@servicetitan/react-ioc';
import { computed, observable, action, runInAction } from 'mobx';
import { getEnumValues, InputFieldState, FormValidators } from '@servicetitan/form';
import Moment from 'moment';
import { FieldState, FormState } from 'formstate';
import {
    ApplicationForm,
    createEmptyApplicationForm,
} from '../components/service-finance/forms/application-information-forms';
import {
    FinancingAPIService,
    FinancingPlanModel,
    LoansAPIService,
    LoanApplication,
    FinancingProvider,
    LoanApplicantIdDocumentType,
    PropertyType,
    ResidenceOwnership,
    FinancingAddress,
    LoanApplicationSubmitSource,
    SubmitApplicationResult,
    SubmitApplicationDecision,
    LoanDocument,
    SignDocumentsData,
} from 'src/api';
import { AxiosError, AxiosResponse } from 'axios';
import { Estimate } from '../../api/model/estimate';
import { CalculatedProgram } from '../../api/model/calculatedProgram';
import { EstimateStore, TenantSettings } from './estimate.store';

export enum ApplicationStage {
    SelectPlan,
    AddInfo,
    ReviewApplication,
    ReviewResponse,
    ESignature,
    SignLoan,
    FinishExternalSignature,
    Completed,
}

export enum ApplicationStageMobile {
    SelectPlan,
    AddInfo,
    AddCoApplicantInfo,
    ReviewApplication,
    PrimaryApplicantReviewApplication,
    CoApplicantReviewApplication,
    ReviewResponse,
    Completed,
}

export enum ApplicationStatus {
    Approved,
    Declined,
    UnderReview,
    CounterOffer,
    NoConsent,
    ConnectionError,
    ProblemProcessingRequest,
    External,
    Complete,
}

export enum ErrorType {
    ConnectionError,
    ProblemProcessingRequest,
}

export interface Offer {
    AmountFinanced: number;
    Term: number;
    Apr: string;
    MonthlyPayment: number;
    MaxCreditAmount: number;
}

export interface IESignAgreementConsent {
    PrimaryApplicant?: boolean;
    CoApplicant?: boolean;
    scrolledToBottom: boolean;
}

export interface NextButtonData {
    nextTitle: string;
    cancelTitle: string;
    action(): void;
    miscTitle?: string;
    miscAction?(): void;
    backAction?(): void;
}

export interface IDetailField {
    title: string;
    value: string;
}

interface DialogData {
    message: string;
    title: string;
    secondaryAction(): void;
    primaryAction(): void;
}

@injectable()
export class ServiceFinanceStore {
    constructor(
        private financingApi: FinancingAPIService,
        private loansApi: LoansAPIService,
        private estimateStore: EstimateStore
    ) {}

    @observable
    applicationIsOpen = true;

    @observable
    isDialogOpen = false;

    @observable
    dialogData?: DialogData | undefined;

    @observable
    applicationStatus?: ApplicationStatus | undefined;

    @observable
    offer?: Offer | undefined;

    @observable
    applicationId?: string | undefined;

    @observable
    externalApplicationId?: string | undefined;

    @observable
    currentStage = ApplicationStage.SelectPlan;

    @observable
    currentStageMobile = ApplicationStageMobile.SelectPlan;

    @observable
    selectedPlan?: CalculatedProgram | undefined;

    @observable
    selectedPlans: CalculatedProgram[] = [];

    @observable
    applicationForm: ApplicationForm | undefined;

    @observable
    selectedProducts: string[] = [];

    @observable
    isApplicationFormValidated = false;

    @observable
    eSignConsentAgreement: IESignAgreementConsent = {
        PrimaryApplicant: undefined,
        CoApplicant: undefined,
        scrolledToBottom: false,
    };

    @observable
    signLoanForm = new FormState({
        applicant: new InputFieldState<string>('').validators(FormValidators.required),
        coApplicant: new InputFieldState<string>(''),
    });

    @observable
    isProcessing = false;

    @observable
    isLoadingPlans = false;

    @observable
    isCoApplicantSelectedMobile = false;

    @observable
    errorMessage: string | null = null;

    @observable
    tenantSettings: TenantSettings | undefined;

    loanDocumentInfo: LoanDocument | undefined;

    onClose: (() => void) | undefined;

    buId: string | undefined;

    tenantId: string | undefined;

    technicianId: string | undefined;

    customerId: string | undefined;

    jobId: string | undefined;

    sourceId: string | undefined;

    backAction: (() => void) | undefined;

    @action
    initialize = async (
        onClose: () => void,
        selectedEstimate: Estimate | undefined,
        backAction?: () => void
    ) => {
        this.currentStage = ApplicationStage.SelectPlan;
        this.currentStageMobile = ApplicationStageMobile.SelectPlan;
        this.applicationForm = createEmptyApplicationForm();
        this.onClose = onClose;
        this.signLoanForm.reset();

        this.buId = selectedEstimate?.buId;
        this.tenantId = selectedEstimate?.tenantId;
        this.technicianId = selectedEstimate?.technicianId;
        this.customerId = selectedEstimate?.customer?.id;
        this.jobId = selectedEstimate?.jobId;
        this.sourceId = selectedEstimate?.id;
        this.backAction = backAction;

        this.tenantSettings = await this.estimateStore.getFinancingSettings(this.tenantId!);

        this.preFillApplicationForm(selectedEstimate!);
    };

    @action
    goToSignaturePage = () => {
        this.currentStage = ApplicationStage.ESignature;
    };

    @action
    goToSignLoan = async () => {
        const startTime = Moment(new Date());
        const retry = async (attempt: () => Promise<void>) => {
            const timeDifference = Moment(new Date()).diff(startTime, 's');
            if (timeDifference >= 30) {
                runInAction(() => {
                    this.signLoanForm.$.applicant.validators();
                    this.signLoanForm.$.coApplicant.validators();
                    const applicantForm = this.applicationForm?.$.PrimaryApplicant.$.PersonalInfo.$;
                    this.signLoanForm.$.applicant.value = `${applicantForm?.FirstName.value} ${applicantForm?.LastName.value}`;
                    this.applicationStatus = ApplicationStatus.External;
                    this.currentStage = ApplicationStage.FinishExternalSignature;
                });
            } else {
                await attempt();
            }
        };
        const tryGettingDocument = async () => {
            try {
                runInAction(() => (this.isProcessing = true));
                const response = await this.financingApi.generateLoanDocument(
                    this.applicationId!,
                    true
                );
                if (!response || response.status >= 400) {
                    retry(tryGettingDocument);
                    return;
                }
                this.loanDocumentInfo = response.data;
                if (this.loanDocumentInfo!.coApplicantSignaturePending) {
                    this.signLoanForm.$.coApplicant.validators(FormValidators.required);
                } else {
                    this.signLoanForm.$.coApplicant.validators();
                }
            } catch (error) {
                retry(tryGettingDocument);
            }
            runInAction(() => (this.isProcessing = false));
        };
        await tryGettingDocument();
    };

    @action
    goToSelectPlanPage = () => {
        this.currentStage = ApplicationStage.SelectPlan;
    };

    @action
    goToAddInfoPage = () => {
        this.currentStage = ApplicationStage.AddInfo;
    };

    @action
    goToNoConsentPage = () => {
        this.applicationStatus = ApplicationStatus.NoConsent;
        this.currentStage = ApplicationStage.Completed;
    };

    @action
    selectPlan = (planNumber: string) => {
        this.selectedPlan = this.selectedPlans.find((p) => p.planNumber === planNumber);
    };

    @action
    unSelectPlan = (planNumber: string) => {
        if (this.selectedPlan && this.selectedPlan.planNumber === planNumber) {
            this.selectedPlan = undefined;
        }
    };

    stopPropagation = (e: React.SyntheticEvent) => {
        e.stopPropagation();
    };

    getSelectedProducts = async () => {
        const businessUnitId = this.buId || '';
        try {
            const productsResult = await this.financingApi.getSelectedProducts(
                'ServiceFinance',
                businessUnitId
            );
            runInAction(() => (this.selectedProducts = productsResult.data));
        } catch (error) {
            this.handleError(error);
        }
    };

    getButtonsDataMobile = (): NextButtonData => {
        switch (this.currentStageMobile) {
            case ApplicationStageMobile.SelectPlan: {
                return {
                    cancelTitle: 'Back',
                    nextTitle: 'Next',
                    action: this.goToNextStepMobile,
                    backAction: this.backAction,
                };
            }
            case ApplicationStageMobile.AddInfo:
            case ApplicationStageMobile.AddCoApplicantInfo: {
                return {
                    cancelTitle: 'Back',
                    nextTitle: 'Next',
                    action: this.goToReviewORAddCoApplicantStepMobile,
                    backAction: this.goToPreviousStepMobile,
                };
            }
            case ApplicationStageMobile.ReviewApplication:
            case ApplicationStageMobile.CoApplicantReviewApplication:
                return {
                    cancelTitle: 'Back',
                    nextTitle: 'Submit',
                    action: this.goToResponseStepMobile,
                    backAction: this.goToAddInfoStepMobile,
                };
            case ApplicationStageMobile.ReviewResponse:
                return {
                    cancelTitle: 'Back',
                    nextTitle: this.getReviewResponseButtonText(),
                    action: this.reviewResponseAction(),
                };
            default:
                return {
                    cancelTitle: 'Back',
                    nextTitle: 'Next',
                    action: this.goToNextStepMobile,
                    backAction: this.goToPreviousStepMobile,
                };
        }
    };

    getReviewResponseButtonText = () => {
        if (this.applicationStatus === undefined) {
            return 'Next';
        }
        return this.applicationStatus === ApplicationStatus.Approved ||
            this.applicationStatus === ApplicationStatus.CounterOffer
            ? 'View Loan Docs in Service Finance'
            : 'Exit';
    };

    reviewResponseAction = (): (() => void) => {
        if (this.applicationStatus === undefined) {
            return this.backAction!;
        }
        return this.applicationStatus === ApplicationStatus.Approved ||
            this.applicationStatus === ApplicationStatus.CounterOffer
            ? this.goToExternalServiceFinancePage
            : this.backAction!;
    };

    getButtonsData = (): NextButtonData => {
        const cancelTitle = [
            ApplicationStage.AddInfo,
            ApplicationStage.SelectPlan,
            ApplicationStage.ReviewApplication,
        ].includes(this.currentStage)
            ? 'Cancel'
            : 'Skip signing the loan document';
        switch (this.currentStage) {
            case ApplicationStage.ReviewResponse:
                const applicationHasAnOffer =
                    this.applicationStatus !== undefined &&
                    [ApplicationStatus.Approved, ApplicationStatus.CounterOffer].includes(
                        this.applicationStatus!
                    );
                return {
                    cancelTitle,
                    nextTitle: applicationHasAnOffer ? 'Continue to Signature' : 'Exit',
                    action: applicationHasAnOffer ? this.goToSignaturePage : this.onClose!,
                    backAction: this.goToPreviousStep,
                };
            case ApplicationStage.SignLoan: {
                return {
                    cancelTitle,
                    nextTitle: 'Submit',
                    action: this.submitSignatures,
                    backAction: this.goToPreviousStep,
                };
            }
            case ApplicationStage.Completed: {
                return {
                    cancelTitle,
                    nextTitle: 'Exit',
                    action: this.onClose!,
                };
            }
            case ApplicationStage.FinishExternalSignature: {
                return {
                    cancelTitle,
                    nextTitle: 'Continue to Service Finance',
                    action: this.goToExternalServiceFinancePage!,
                    miscTitle: 'Next',
                    miscAction: this.showFinalizeLoanDialog,
                    backAction: this.goToPreviousStep,
                };
            }
            case ApplicationStage.SelectPlan: {
                return {
                    cancelTitle,
                    nextTitle: 'Next',
                    action: this.goToNextStep,
                    backAction: this.backAction,
                };
            }
            default:
                return {
                    cancelTitle,
                    nextTitle: 'Next',
                    action: this.goToNextStep,
                    backAction: this.goToPreviousStep,
                };
        }
    };

    @action
    closeDialog = () => {
        this.isDialogOpen = false;
    };

    @action
    showFinalizeLoanDialog = () => {
        this.dialogData = {
            title: 'Finalize Loan',
            message: 'Was the loan document successfully signed through Service Finance?',
            primaryAction: () => {
                this.applicationStatus = ApplicationStatus.Complete;
                this.currentStage = ApplicationStage.Completed;
                this.isDialogOpen = false;
                this.submitSignatures();
            },
            secondaryAction: () => {
                this.applicationStatus = ApplicationStatus.Complete;
                this.currentStage = ApplicationStage.Completed;
                this.isDialogOpen = false;
            },
        };
        this.isDialogOpen = true;
    };

    @action
    goToPreviousStep = () => {
        const stages = getEnumValues(ApplicationStage);
        const currentIndex = stages.findIndex((s) => s === this.currentStage);
        this.currentStage = Math.min(stages[currentIndex - 1], 0);
    };

    @action
    goToNextStep = async () => {
        if (this.currentStage === ApplicationStage.AddInfo) {
            await this.setIsApplicationFormValidated();
        }

        if (!this.isNextButtonEnabled) {
            return;
        }

        if (this.currentStage === ApplicationStage.ReviewApplication) {
            await this.submitApplication();
        }

        if (this.currentStage === ApplicationStage.ESignature) {
            const hasCoApplicant = this.applicationForm!.$.hasCoApplicant.value;
            const { PrimaryApplicant, CoApplicant } = this.eSignConsentAgreement;

            if (PrimaryApplicant && (!hasCoApplicant || CoApplicant)) {
                await this.goToSignLoan();
            } else {
                this.goToNoConsentPage();
                return;
            }
        }

        const stages = getEnumValues(ApplicationStage);
        const currentIndex = stages.findIndex((s) => s === this.currentStage);
        this.currentStage = Math.min(stages[currentIndex + 1], stages.length - 1);
    };

    @action
    goToNextStepMobile = async () => {
        if (!this.isNextButtonEnabledMobile) {
            return;
        }

        const stages = getEnumValues(ApplicationStageMobile);
        const currentIndex = stages.findIndex((s) => s === this.currentStageMobile);
        this.currentStageMobile = Math.min(stages[currentIndex + 1], stages.length - 1);
    };

    @action
    goToPreviousStepMobile = async () => {
        if (this.currentStageMobile === ApplicationStageMobile.AddCoApplicantInfo) {
            // To unblock the next button in Add Info page.
            this.applicationForm!.$.hasCoApplicant.onChange(false);
            await this.setIsApplicationFormValidated();
        }
        const stages = getEnumValues(ApplicationStageMobile);
        const currentIndex = stages.findIndex((s) => s === this.currentStageMobile);
        this.currentStageMobile = Math.max(stages[currentIndex - 1], 0);
    };

    @action
    goToAddInfoStepMobile = () => {
        if (this.isCoApplicantSelectedMobile) {
            this.currentStageMobile = ApplicationStageMobile.AddCoApplicantInfo;
        } else {
            this.currentStageMobile = ApplicationStageMobile.AddInfo;
        }
    };

    goToResponseStepMobile = async () => {
        await this.submitApplication();
        runInAction(() => {
            this.currentStageMobile = ApplicationStageMobile.ReviewResponse;
        });
    };

    @action
    goToReviewORAddCoApplicantStepMobile = async () => {
        if (this.currentStageMobile === ApplicationStageMobile.AddInfo) {
            this.applicationForm!.$.hasCoApplicant.onChange(this.isCoApplicantSelectedMobile);
            if (this.isCoApplicantSelectedMobile) {
                this.currentStageMobile = ApplicationStageMobile.AddCoApplicantInfo;
                await this.setIsApplicationFormValidated();
            } else {
                this.currentStageMobile = ApplicationStageMobile.ReviewApplication;
            }
        } else if (this.currentStageMobile === ApplicationStageMobile.AddCoApplicantInfo) {
            this.currentStageMobile = ApplicationStageMobile.PrimaryApplicantReviewApplication;
        }
    };

    @action
    setIsCoApplicantSelectedMobile = (value: boolean) => {
        this.isCoApplicantSelectedMobile = value;
    };

    submitSignatures = async () => {
        const signModel: SignDocumentsData = {
            loanId: this.applicationId!,
            signedDocument: {
                primaryApplicantSignature: this.signLoanForm.$.applicant.value,
                coApplicantSignature:
                    this.signLoanForm.$.coApplicant.value === ''
                        ? undefined
                        : this.signLoanForm.$.coApplicant.value,
            },
        };

        try {
            runInAction(() => (this.isProcessing = true));
            const response = await this.financingApi.signDocument(
                this.applicationId!,
                signModel,
                true
            );
            if (response.status >= 200 && response.status < 300) {
                runInAction(() => {
                    this.applicationStatus = ApplicationStatus.Complete;
                    this.currentStage = ApplicationStage.Completed;
                });
            }
        } catch (error) {
            this.handleError(error);
        }
        runInAction(() => (this.isProcessing = false));
    };

    submitApplication = async () => {
        const loanApp = this.mapModel();
        try {
            runInAction(() => (this.isProcessing = true));
            const response = await this.financingApi.submitApplication(loanApp);
            this.processSubmitResponse(response);
        } catch (error) {
            this.handleError(error);
        }
        runInAction(() => (this.isProcessing = false));
    };

    processSubmitResponse = (response: AxiosResponse<SubmitApplicationResult>) => {
        switch (response.status) {
            case 201: {
                this.processCreated(response.data);
                break;
            }
            case 400: {
                console.log(response);
                // todo extract ErrorInfo[] and show validation failure and handle...
                break;
            }
            case 500: {
                console.log(response);
                // todo extract ErrorInfo[] to see if it's a servicefinance error or other and display
                // accordingly
                break;
            }
        }
    };

    @action
    processCreated = (result: SubmitApplicationResult) => {
        this.applicationId = result.loanId;
        this.externalApplicationId = result.externalLoanId;
        this.offer = {
            AmountFinanced: result.approvedAmount!,
            Apr: String(result.rate!),
            MaxCreditAmount: result.maxCreditAmount ?? result.approvedAmount!,
            MonthlyPayment: result.payment!,
            Term: result.term!,
        };

        if (result.decision === SubmitApplicationDecision.Declined) {
            this.applicationStatus = ApplicationStatus.Declined;
            return;
        }

        if (result.decision == SubmitApplicationDecision.Approved) {
            this.applicationStatus = ApplicationStatus.Approved;
            return;
        }

        if (result.decision == SubmitApplicationDecision.Review) {
            this.applicationStatus = ApplicationStatus.UnderReview;
            return;
        }

        this.applicationStatus = ApplicationStatus.CounterOffer;
    };

    goToExternalServiceFinancePage = () => {
        window.open('https://esign.svcfin.com', '_blank');
    };

    preFillApplicationForm = (estimate: Estimate) => {
        this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.FirstName.reset(
            estimate?.customer?.firstName
        );
        this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.MiddleName.reset(
            estimate?.customer?.middleName
        );
        this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.LastName.reset(
            estimate?.customer?.lastName
        );

        this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.Street.reset(
            estimate?.customer?.address?.streetAddress
        );
        this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.Unit.reset(
            estimate?.customer?.address?.unit
        );
        this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.City.reset(
            estimate?.customer?.address?.city
        ),
            this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.State.reset(
                estimate?.customer?.address?.state
            ),
            this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.Zip.reset(
                estimate?.customer?.address?.zip
            );
    };

    mapModel = (): LoanApplication => {
        const salesAmount = Number(this.applicationForm!.$.Loan.$.SalesAmount.value);
        const downPayment = Number(this.applicationForm!.$.Loan.$.DownPayment.value || '0');
        const requestedAmount = Number((salesAmount - downPayment).toFixed(2));

        const monthlyHousingPayment = Number(
            this.applicationForm!.$.Residence.$.MonthlyPayment.value
        );
        const grossMonthlyIncome = Number(
            this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.GrossMonthlyIncome.value
        );
        const applicantAddress: FinancingAddress = {
            street: this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.Street.value,
            address2: this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.Unit.value,
            city: this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.City.value,
            state: this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.State.value,
            zip: this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.Zip.value,
        };
        const propertyAddress: FinancingAddress = this.applicationForm!.$
            .WorkingAddressSameAsPrimaryAddress.value
            ? applicantAddress
            : {
                  street: this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.Street.value,
                  address2: this.applicationForm!.$.WorkingAddress.$.Unit.value,
                  city: this.applicationForm!.$.WorkingAddress.$.City.value,
                  state: this.applicationForm!.$.WorkingAddress.$.State.value,
                  zip: this.applicationForm!.$.WorkingAddress.$.Zip.value,
              };
        const coApplicatAddress = this.applicationForm!.$.hasCoApplicant.value
            ? this.applicationForm!.$.CoApplicant.$.SameAsPrimaryAddress
                ? applicantAddress
                : {
                      street: this.applicationForm!.$.CoApplicant.$.HomeAddress.$.Street.value,
                      address2: this.applicationForm!.$.CoApplicant.$.HomeAddress.$.Unit.value,
                      city: this.applicationForm!.$.CoApplicant.$.HomeAddress.$.City.value,
                      state: this.applicationForm!.$.CoApplicant.$.HomeAddress.$.State.value,
                      zip: this.applicationForm!.$.CoApplicant.$.HomeAddress.$.Zip.value,
                  }
            : {};
        const loanApp: LoanApplication = {
            buId: this.buId,
            tenantId: this.tenantId,
            customerId: this.customerId,
            jobId: this.jobId,
            technicianId: this.technicianId,
            salesAmount,
            downPayment,
            requestedAmount,
            address: propertyAddress,
            improvement: this.applicationForm!.$.Loan.$.Product.value,
            planNumber: String(this.selectedPlan!.planNumber),
            primaryApplicant: {
                monthlyHousingPayment,
                propertyType: this.mapPropertyType(),
                housingType: this.applicationForm!.$.Residence.$.HousingType
                    .value as ResidenceOwnership,
                firstName:
                    this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.FirstName.value,
                lastName: this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.LastName.value,
                middleInitial:
                    this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.MiddleName.value,
                dateOfBirth:
                    this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.DateOfBirth.value,
                isMarried: this.applicationForm!.$.Marriage.$.isMarried.value,
                emailAddress: this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.Email.value,
                primaryPhone:
                    this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.PhoneNumber.value.replace(
                        / |-|\(|\)/g,
                        ''
                    ),
                ssn: this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.Ssn.value.replace(
                    /-/g,
                    ''
                ),
                address: applicantAddress,
                employment: {
                    grossMonthlyIncome,
                    employerName:
                        this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.EmployerName
                            .value,
                },
                applicantId: {
                    applicantIdDocumentType: this.parseDocumentType(
                        this.applicationForm!.$.PrimaryApplicant.$.Document.$.Type.value
                    ),
                    expirationDate:
                        this.applicationForm!.$.PrimaryApplicant.$.Document.$.ExpirationDate.value,
                    issueDate:
                        this.applicationForm!.$.PrimaryApplicant.$.Document.$.IssueDate.value,
                    documentId: this.applicationForm!.$.PrimaryApplicant.$.Document.$.Number.value,
                    state: this.applicationForm!.$.PrimaryApplicant.$.Document.$.State.value,
                },
                spouseContact: this.applicationForm!.$.Marriage.$.isMarried.value
                    ? this.applicationForm!.$.Marriage.$.ContactInfo.value
                    : undefined,
                isUSCitizen:
                    this.applicationForm!.$.PrimaryApplicant.$.PersonalInfo.$.IsUSCitizen.value,
            },
            coApplicant: this.applicationForm!.$.hasCoApplicant.value
                ? {
                      firstName:
                          this.applicationForm!.$.CoApplicant.$.PersonalInfo.$.FirstName.value,
                      lastName: this.applicationForm!.$.CoApplicant.$.PersonalInfo.$.LastName.value,
                      middleInitial:
                          this.applicationForm!.$.CoApplicant.$.PersonalInfo.$.MiddleName.value,
                      dateOfBirth:
                          this.applicationForm!.$.CoApplicant.$.PersonalInfo.$.DateOfBirth.value,
                      emailAddress:
                          this.applicationForm!.$.CoApplicant.$.PersonalInfo.$.Email.value,
                      housingType: ResidenceOwnership.NotSet,
                      monthlyHousingPayment: 0,
                      propertyType: PropertyType.NotSet,
                      primaryPhone:
                          this.applicationForm!.$.CoApplicant.$.PersonalInfo.$.PhoneNumber.value.replace(
                              / |-|\(|\)/g,
                              ''
                          ),
                      ssn: this.applicationForm!.$.CoApplicant.$.PersonalInfo.$.Ssn.value.replace(
                          /-/g,
                          ''
                      ),
                      address: coApplicatAddress,
                      employment: {
                          grossMonthlyIncome,
                          employerName:
                              this.applicationForm!.$.CoApplicant.$.PersonalInfo.$.EmployerName
                                  .value,
                      },
                      isUSCitizen:
                          this.applicationForm!.$.CoApplicant.$.PersonalInfo.$.IsUSCitizen.value,
                      applicantId: {
                          applicantIdDocumentType: this.parseDocumentType(
                              this.applicationForm!.$.CoApplicant.$.Document.$.Type.value
                          ),
                          expirationDate:
                              this.applicationForm!.$.CoApplicant.$.Document.$.ExpirationDate.value,
                          issueDate:
                              this.applicationForm!.$.CoApplicant.$.Document.$.IssueDate.value,
                          documentId: this.applicationForm!.$.CoApplicant.$.Document.$.Number.value,
                          state: this.applicationForm!.$.CoApplicant.$.Document.$.State.value,
                      },
                  }
                : undefined,
            providerName: FinancingProvider.ServiceFinance,
            source: LoanApplicationSubmitSource.OnlineEstimates,
            sourceId: this.sourceId || null,
        };

        return loanApp;
    };

    mapPropertyType = (): PropertyType => {
        if (this.applicationForm!.$.Residence.$.PropertyType.value === 'Single Family') {
            return PropertyType.SingleFamily;
        }

        return PropertyType.ManufacturedHome;
    };

    parseDocumentType = (documentType: string): LoanApplicantIdDocumentType => {
        switch (documentType) {
            case 'Driver License': {
                return LoanApplicantIdDocumentType.DriversLicense;
            }
            case 'Military ID': {
                return LoanApplicantIdDocumentType.MilitaryId;
            }
            case 'State ID': {
                return LoanApplicantIdDocumentType.StateId;
            }
            case 'USA Green Card': {
                return LoanApplicantIdDocumentType.UsaGreenCard;
            }
            case 'Passport': {
                return LoanApplicantIdDocumentType.Passport;
            }
            default: {
                return LoanApplicantIdDocumentType.NotSet;
            }
        }
    };

    getRequestLoanAmount = (): number => {
        const salesAmount = Number(this.applicationForm!.$.Loan.$.SalesAmount.value);
        const downPayment = Number(this.applicationForm!.$.Loan.$.DownPayment.value);
        const requestedAmount = salesAmount - downPayment;
        return requestedAmount;
    };

    @computed
    get isConsentAgreementValidated(): boolean {
        if (
            !this.eSignConsentAgreement.scrolledToBottom ||
            this.eSignConsentAgreement.PrimaryApplicant === undefined ||
            (this.applicationForm!.$.hasCoApplicant.value &&
                this.eSignConsentAgreement.CoApplicant === undefined)
        ) {
            return false;
        }
        return true;
    }

    @computed
    get isNextButtonEnabled(): boolean {
        switch (this.currentStage) {
            case ApplicationStage.SelectPlan:
                return !!this.selectedPlan;
            case ApplicationStage.AddInfo:
                return this.isApplicationFormValidated;
            case ApplicationStage.ESignature:
                return this.isConsentAgreementValidated;
            case ApplicationStage.SignLoan:
                return !this.signLoanForm.hasError;
            default:
                return true;
        }
    }

    @computed
    get isNextButtonEnabledMobile(): boolean {
        switch (this.currentStageMobile) {
            case ApplicationStageMobile.SelectPlan:
                return !!this.selectedPlan;
            case ApplicationStageMobile.AddInfo:
                return this.isApplicationFormValidated;
            case ApplicationStageMobile.AddCoApplicantInfo:
                return this.isApplicationFormValidated;
            default:
                return true;
        }
    }

    @computed
    get isMaritalStatusRequired(): boolean {
        const state = this.applicationForm!.$.PrimaryApplicant.$.HomeAddress.$.State.value;
        return state === 'WI' || state === 'MO';
    }

    @computed
    get isLoanAmountValid(): boolean {
        const saleAmount = this.applicationForm!.$.Loan.$.SalesAmount.value;
        const downPayment = this.applicationForm!.$.Loan.$.DownPayment.value || '0';
        if (!this.selectedPlan || !saleAmount || !downPayment) {
            return true;
        }
        const minimumAmount = this.selectedPlan.minLoanAmount;
        return parseInt(saleAmount, 10) - parseInt(downPayment, 10) - minimumAmount! >= 0;
    }

    @computed
    get desiredAmount(): number {
        const saleAmount = this.applicationForm!.$.Loan.$.SalesAmount.value;
        const downPayment = this.applicationForm!.$.Loan.$.DownPayment.value || '0';
        return parseFloat(saleAmount) - parseFloat(downPayment);
    }

    @computed
    get addingApplicantInfoMobile(): boolean {
        return (
            this.currentStageMobile === ApplicationStageMobile.AddInfo ||
            this.currentStageMobile === ApplicationStageMobile.AddCoApplicantInfo
        );
    }

    getLoanDetails = (): IDetailField[] => {
        const { Loan } = this.applicationForm!.$;
        return [
            { title: 'Loan Amount($)', value: Loan.$.SalesAmount.value! },
            { title: 'Down Payment($)', value: Loan.$.DownPayment.value || '0' },
            { title: 'Improvement Type', value: Loan.$.Product.value },
        ];
    };

    getLoanDetailsMobile = (): IDetailField[][] => {
        const { Loan } = this.applicationForm!.$;
        const { Document } = this.applicationForm!.$.PrimaryApplicant.$;
        return [
            [
                { title: 'Loan Amount($)', value: Loan.$.SalesAmount.value! },
                { title: 'Down Payment($)', value: Loan.$.DownPayment.value },
            ],
            [
                { title: 'Improvement Type', value: Loan.$.Product.value },
                { title: 'Job State', value: Document.$.State.value },
            ],
        ];
    };

    getApplicantDetails = (): IDetailField[][] => {
        const { PersonalInfo, Document, HomeAddress } = this.applicationForm!.$.PrimaryApplicant.$;
        const { Residence, hasCoApplicant } = this.applicationForm!.$;
        return [
            [
                {
                    title: 'Borrower Name',
                    value: `${PersonalInfo.$.FirstName.value} ${PersonalInfo.$.LastName.value}`,
                },
                { title: 'Email Address', value: PersonalInfo.$.Email.value },
                { title: 'Birthday', value: PersonalInfo.$.DateOfBirth.value },
                { title: 'SSN', value: PersonalInfo.$.DisplaySsn.value },
            ],
            [
                { title: 'US Citizen', value: PersonalInfo.$.IsUSCitizen.value ? 'Yes' : 'No' },
                { title: 'ID Type', value: Document.$.Type.value },
                { title: 'Document Number', value: Document.$.Number.value },
                { title: 'State', value: Document.$.State.value || 'N/A' },
            ],
            [
                { title: 'Issue Date', value: Document.$.IssueDate.value },
                { title: 'Expiration Date', value: Document.$.ExpirationDate.value },
                { title: 'Phone Number', value: PersonalInfo.$.PhoneNumber.value },
                { title: 'Street Address', value: HomeAddress.$.Street.value },
            ],
            [
                { title: 'Own or Rent', value: Residence.$.HousingType.value },
                { title: 'Monthly Rent Amount', value: Residence.$.MonthlyPayment.value },
                { title: 'Property Type', value: Residence.$.PropertyType.value },
                { title: 'Employer Name', value: PersonalInfo.$.EmployerName.value },
            ],
            [
                {
                    title: 'Gross Monthly Incomes($)',
                    value: PersonalInfo.$.GrossMonthlyIncome.value,
                },
                { title: 'Co-Applicant', value: hasCoApplicant.value ? 'Yes' : 'No' },
            ],
        ];
    };

    getApplicantDetailsMobile = (): IDetailField[][] => {
        const { PersonalInfo, Document, HomeAddress } = this.applicationForm!.$.PrimaryApplicant.$;
        const { Residence, hasCoApplicant } = this.applicationForm!.$;
        return [
            [
                {
                    title: 'Borrower Name',
                    value: `${PersonalInfo.$.FirstName.value} ${PersonalInfo.$.LastName.value}`,
                },
            ],
            [{ title: 'Email Address', value: PersonalInfo.$.Email.value }],
            [{ title: 'Date of Birth', value: PersonalInfo.$.DateOfBirth.value }],
            [{ title: 'ID Type', value: Document.$.Type.value }],
            [
                { title: 'Document Number', value: Document.$.Number.value },
                { title: 'State', value: Document.$.State.value || 'N/A' },
            ],
            [
                { title: 'Expiration Date', value: Document.$.ExpirationDate.value },
                { title: 'Issue Date', value: Document.$.IssueDate.value },
            ],
            [{ title: 'Social Security Number', value: PersonalInfo.$.DisplaySsn.value }],
            [{ title: 'Phone Number', value: PersonalInfo.$.PhoneNumber.value }],
            [{ title: 'Street Address', value: HomeAddress.$.Street.value }],
            [{ title: 'Unit', value: HomeAddress.$.Unit.value }],
            [
                { title: 'City', value: HomeAddress.$.City.value },
                { title: 'State', value: HomeAddress.$.State.value },
            ],
            [
                { title: 'Own or Rent', value: Residence.$.HousingType.value },
                { title: 'Monthly Rent Amount', value: Residence.$.MonthlyPayment.value },
            ],
            [
                { title: 'Property Type', value: Residence.$.PropertyType.value },
                { title: 'Zip Code', value: HomeAddress.$.Zip.value },
            ],
            [
                {
                    title: 'Gross Monthly Incomes($)',
                    value: PersonalInfo.$.GrossMonthlyIncome.value,
                },
                { title: 'Co-Applicant', value: hasCoApplicant.value ? 'Yes' : 'No' },
            ],
            [{ title: 'Employer Name', value: PersonalInfo.$.EmployerName.value }],
        ];
    };

    getCoApplicantDetails = (): IDetailField[][] => {
        if (!this.applicationForm!.$.hasCoApplicant.value) {
            return [];
        } else {
            const { PersonalInfo, Document, HomeAddress, SameAsPrimaryAddress } =
                this.applicationForm!.$.CoApplicant.$;
            const { HomeAddress: PrimaryAddress } = this.applicationForm!.$.PrimaryApplicant.$;
            const address = SameAsPrimaryAddress.value ? PrimaryAddress : HomeAddress;
            return [
                [
                    {
                        title: 'Borrower Name',
                        value: `${PersonalInfo.$.FirstName.value} ${PersonalInfo.$.LastName.value}`,
                    },
                    { title: 'Email Address', value: PersonalInfo.$.Email.value },
                    { title: 'Birthday', value: PersonalInfo.$.DateOfBirth.value },
                    { title: 'SSN', value: PersonalInfo.$.DisplaySsn.value },
                ],
                [
                    { title: 'US Citizen', value: PersonalInfo.$.IsUSCitizen.value ? 'Yes' : 'No' },
                    { title: 'ID Type', value: Document.$.Type.value },
                    { title: 'Document Number', value: Document.$.Number.value },
                    { title: 'State', value: Document.$.State.value || 'N/A' },
                ],
                [
                    { title: 'Issue Date', value: Document.$.IssueDate.value },
                    { title: 'Expiration Date', value: Document.$.ExpirationDate.value },
                    { title: 'Phone Number', value: PersonalInfo.$.PhoneNumber.value },
                    { title: 'Street Address', value: address.$.Street.value },
                ],
                [
                    { title: 'Employer Name', value: PersonalInfo.$.EmployerName.value },
                    {
                        title: 'Gross Monthly Incomes($)',
                        value: PersonalInfo.$.GrossMonthlyIncome.value,
                    },
                ],
            ];
        }
    };

    getCoApplicantDetailsMobile = (): IDetailField[][] => {
        if (!this.applicationForm!.$.hasCoApplicant.value) {
            return [];
        } else {
            const { PersonalInfo, Document, HomeAddress, SameAsPrimaryAddress } =
                this.applicationForm!.$.CoApplicant.$;
            const { HomeAddress: PrimaryAddress } = this.applicationForm!.$.PrimaryApplicant.$;
            const address = SameAsPrimaryAddress.value ? PrimaryAddress : HomeAddress;
            const { Residence } = this.applicationForm!.$;
            return [
                [
                    {
                        title: 'Borrower Name',
                        value: `${PersonalInfo.$.FirstName.value} ${PersonalInfo.$.LastName.value}`,
                    },
                ],
                [{ title: 'Email Address', value: PersonalInfo.$.Email.value }],
                [{ title: 'Date of Birth', value: PersonalInfo.$.DateOfBirth.value }],
                [{ title: 'ID Type', value: Document.$.Type.value }],
                [
                    { title: 'Document Number', value: Document.$.Number.value },
                    { title: 'State', value: Document.$.State.value || 'N/A' },
                ],
                [
                    { title: 'Expiration Date', value: Document.$.ExpirationDate.value },
                    { title: 'Issue Date', value: Document.$.IssueDate.value },
                ],
                [{ title: 'Social Security Number', value: PersonalInfo.$.DisplaySsn.value }],
                [{ title: 'Phone Number', value: PersonalInfo.$.PhoneNumber.value }],
                [{ title: 'Street Address', value: address.$.Street.value }],
                [{ title: 'Unit', value: address.$.Unit.value }],
                [
                    { title: 'City', value: address.$.City.value },
                    { title: 'State', value: address.$.State.value },
                ],
                [
                    { title: 'Own or Rent', value: Residence.$.HousingType.value },
                    { title: 'Monthly Rent Amount', value: Residence.$.MonthlyPayment.value },
                ],
                [
                    { title: 'Property Type', value: Residence.$.PropertyType.value },
                    { title: 'Zip Code', value: address.$.Zip.value },
                ],
                [
                    {
                        title: 'Gross Monthly Incomes($)',
                        value: PersonalInfo.$.GrossMonthlyIncome.value,
                    },
                ],
                [{ title: 'Employer Name', value: PersonalInfo.$.EmployerName.value }],
            ];
        }
    };

    @action
    setIsApplicationFormValidated = async () => {
        // Loan amount validation is not included in the application form validators.
        this.isApplicationFormValidated =
            this.isLoanAmountValid && (await this.validateApplicationForm());
    };

    validateApplicationForm = async (): Promise<boolean> => {
        if ((await this.applicationForm!.$.Loan.validate()).hasError) {
            return false;
        }
        if ((await this.applicationForm!.$.PrimaryApplicant.validate()).hasError) {
            return false;
        }
        const documentType = this.parseDocumentType(
            this.applicationForm!.$.PrimaryApplicant.$.Document.$.Type.value
        );
        const documentState = this.applicationForm!.$.PrimaryApplicant.$.Document.$.State.value;
        if (documentType !== LoanApplicantIdDocumentType.MilitaryId && !documentState) {
            return false;
        }
        if (
            !this.applicationForm!.$.WorkingAddressSameAsPrimaryAddress.value &&
            (await this.applicationForm!.$.WorkingAddress.validate()).hasError
        ) {
            return false;
        }
        if (
            this.isMaritalStatusRequired &&
            this.applicationForm!.$.Marriage.$.isMarried.value &&
            (await this.applicationForm!.$.Marriage.$.ContactInfo.validate()).hasError
        ) {
            return false;
        }
        if ((await this.applicationForm!.$.Residence.validate()).hasError) {
            return false;
        }
        if (this.applicationForm!.$.hasCoApplicant.value) {
            const coApplicant = this.applicationForm!.$.CoApplicant;
            if ((await coApplicant.$.Document.validate()).hasError) {
                return false;
            }
            if ((await coApplicant.$.PersonalInfo.validate()).hasError) {
                return false;
            }
            if (
                !coApplicant.$.SameAsPrimaryAddress.value &&
                (await coApplicant.$.HomeAddress.validate()).hasError
            ) {
                return false;
            }
        }
        return true;
    };

    validateField = (fieldState?: FieldState<string>) => {
        if (!fieldState) {
            return async () => await this.setIsApplicationFormValidated();
        }
        return async () => {
            await fieldState.validate();
            await this.setIsApplicationFormValidated();
        };
    };

    @action
    showAllPlans = async (programName: string, totalPrice: number, stateCode: string) => {
        this.isLoadingPlans = true;
        const tenantId = this.tenantId || '';
        const businessUnitId = this.buId || '';
        const calculation = await this.loansApi.calculate(
            totalPrice,
            stateCode,
            tenantId,
            businessUnitId,
            true
        );
        let loans: CalculatedProgram[] = calculation?.data;
        runInAction(async () => {
            if (loans) {
                if (this.tenantSettings?.hideIneligiblePlans) {
                    loans = loans.filter((l) => l.eligibleForApplication);
                }
                this.selectedPlans = loans;
            }
            this.isLoadingPlans = false;
        });
    };

    @action
    handleError = (error: any) => {
        if (error.response) {
            this.applicationStatus = ApplicationStatus.ProblemProcessingRequest;

            // Try to parse the error message. Normally, the errors should be
            // a JSON array of objects, each with a 'description' property.
            // But really it could be anything.
            const data = error.response.data;
            let parsedData = null;
            try {
                parsedData = JSON.parse(data);
            } catch (e) {
                // It's not JSON. We'll handle that later.
            }

            if (Array.isArray(parsedData) && parsedData.length > 0 && parsedData[0].description) {
                this.errorMessage = parsedData[0].description;
            } else if (parsedData?.description) {
                this.errorMessage = parsedData.description;
            } else if (data && data.length > 0) {
                // Hopefully the response is a human-readable string.
                this.errorMessage = data;
            } else {
                // If all else fails, use the generic HTTP status text.
                this.errorMessage = error.response.statusText;
            }
        } else {
            // No response was received.
            this.applicationStatus = ApplicationStatus.ConnectionError;
            this.errorMessage = null;
        }
    };

    getStates = () => {
        return [
            'AL',
            'AK',
            'AS',
            'AZ',
            'AR',
            'CA',
            'CO',
            'CT',
            'DE',
            'DC',
            'FM',
            'FL',
            'GA',
            'GU',
            'HI',
            'ID',
            'IL',
            'IN',
            'IA',
            'KS',
            'KY',
            'LA',
            'ME',
            'MH',
            'MD',
            'MA',
            'MI',
            'MN',
            'MS',
            'MO',
            'MT',
            'NE',
            'NV',
            'NH',
            'NJ',
            'NM',
            'NY',
            'NC',
            'ND',
            'MP',
            'OH',
            'OK',
            'OR',
            'PW',
            'PA',
            'PR',
            'RI',
            'SC',
            'SD',
            'TN',
            'TX',
            'UT',
            'VT',
            'VI',
            'VA',
            'WA',
            'WV',
            'WI',
            'WY',
        ];
    };
}
